"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Bell,
  Plus,
  Syringe,
  MoreVertical,
  Edit,
  Trash2,
  Scissors,
  Stethoscope,
  AlertCircle,
  Apple,
  UserRound,
  Smile,
  Activity,
  Scan,
  TestTube,
  Pill,
  HeartPulse,
  Dumbbell,
  Sparkles,
  Brain,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchServices,
  createService,
  updateService,
  deleteService,
} from "../utils/servicesUtils";
import { CURRENCY_OPTIONS } from "../constants/CURRENCY_OPTIONS";

const categoryOptions = [
  { value: "vaccination", label: "Vaccination", icon: Syringe },
  { value: "surgery", label: "Surgery", icon: Scissors },
  { value: "dermatology", label: "Dermatology", icon: Stethoscope },
  { value: "emergency", label: "Emergency", icon: AlertCircle },
  { value: "nutrition", label: "Nutrition", icon: Apple },
  { value: "consultation", label: "Consultation", icon: UserRound },
  { value: "dental", label: "Dental", icon: Smile },
  { value: "grooming", label: "Grooming", icon: Scissors },
  { value: "checkup", label: "Checkup", icon: Activity },
  { value: "radiology", label: "Radiology", icon: Scan },
  { value: "laboratory", label: "Laboratory", icon: TestTube },
  { value: "pharmacy", label: "Pharmacy", icon: Pill },
  { value: "medical", label: "Medical", icon: HeartPulse },
  { value: "training", label: "Training", icon: Dumbbell },
  { value: "wellness", label: "Wellness", icon: Sparkles },
  { value: "behavioral", label: "Behavioral", icon: Brain },
];

const Button = ({
  children,
  onClick,
  variant = "primary",
  icon: Icon,
  size = "medium",
  style = {},
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const s = styles.button;
  const variantStyle = s.variants[variant];
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...s.base(size),
        ...variantStyle,
        ...(isHovered ? variantStyle.hover : {}),
        ...style,
      }}
    >
      {Icon && <Icon style={s.icon} />}
      {children}
    </button>
  );
};

const Modal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const s = styles.modal;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.container} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h2 style={s.title}>{title}</h2>
          <button style={s.closeButton} onClick={onClose}>
            <X style={s.closeIcon} />
          </button>
        </div>
        <div style={s.content}>{children}</div>
      </div>
    </div>
  );
};

const Dropdown = ({ trigger, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const s = styles.dropdown;
  return (
    <div style={s.container} ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && <div style={s.menu}>{children}</div>}
    </div>
  );
};

const DropdownItem = ({
  icon: Icon,
  children,
  onClick,
  variant = "default",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const s = styles.dropdownItem;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ ...s.base(variant, isHovered) }}
    >
      {Icon && <Icon style={s.icon} />}
      {children}
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const s = styles.pagination;
  return (
    <div style={s.container}>
      <div style={s.info}>
        Showing {startItem} to {endItem} of {totalItems} results
      </div>
      <div style={s.buttons}>
        <button
          onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...s.button,
            ...(currentPage === 1 ? s.buttonDisabled : {}),
          }}
        >
          <ChevronLeft style={s.icon} />
          Previous
        </button>
        <button
          onClick={() =>
            currentPage < totalPages && onPageChange(currentPage + 1)
          }
          disabled={currentPage === totalPages}
          style={{
            ...s.button,
            ...(currentPage === totalPages ? s.buttonDisabled : {}),
          }}
        >
          Next
          <ChevronRight style={s.icon} />
        </button>
      </div>
    </div>
  );
};

const ServiceModal = ({
  service,
  onChange,
  onSave,
  onCancel,
  error,
  focusedInput,
  onFocusChange,
  isEdit = false,
  getCurrencySymbol,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedCategory = categoryOptions.find(
    (cat) => cat.value === service.category,
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleCategorySelect = (category) => {
    onChange("category", category.value);
    setIsDropdownOpen(false);
  };
  const s = styles.serviceModal;
  const inputStyle = (field) => ({
    ...s.input,
    ...(focusedInput === field ? s.inputFocused : {}),
  });
  return (
    <div style={s.form}>
      <div style={s.inputGroup}>
        <label style={s.label}>
          Service Name <span style={s.required}>*</span>
        </label>
        <input
          type="text"
          value={service.name}
          onChange={(e) => onChange("name", e.target.value)}
          onFocus={() => onFocusChange?.("name")}
          onBlur={() => onFocusChange?.(null)}
          style={inputStyle("name")}
          placeholder="Enter service name"
        />
      </div>
      <div style={s.inputGroup}>
        <label style={s.label}>
          Category <span style={s.required}>*</span>
        </label>
        <div style={s.dropdownContainer} ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            onFocus={() => onFocusChange?.("category")}
            onBlur={() => onFocusChange?.(null)}
            style={{
              ...s.dropdownButton,
              ...(focusedInput === "category" || isDropdownOpen
                ? s.inputFocused
                : {}),
            }}
          >
            <div style={s.dropdownContent}>
              {selectedCategory ? (
                <>
                  <selectedCategory.icon style={s.dropdownIcon} />
                  <span style={s.dropdownText(true)}>
                    {selectedCategory.label}
                  </span>
                </>
              ) : (
                <span style={s.dropdownText(false)}>Select a category</span>
              )}
            </div>
            <ChevronDown
              style={{ ...s.chevron, ...(isDropdownOpen ? s.chevronOpen : {}) }}
            />
          </button>
          {isDropdownOpen && (
            <div style={s.dropdownMenu}>
              {categoryOptions.map((cat) => {
                const isSelected = service.category === cat.value;
                return (
                  <div
                    key={cat.value}
                    onClick={() => handleCategorySelect(cat)}
                    style={{
                      ...s.dropdownItem,
                      ...(isSelected ? s.dropdownItemSelected : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.backgroundColor =
                          s.dropdownItemHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <cat.icon style={s.dropdownItemIcon} />
                    <span style={s.dropdownItemText}>{cat.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div style={s.inputGroup}>
        <label style={s.label}>
          Service Fee ({getCurrencySymbol()}) <span style={s.required}>*</span>
        </label>
        <input
          type="number"
          value={service.fee}
          onChange={(e) => onChange("fee", e.target.value)}
          onFocus={() => onFocusChange?.("fee")}
          onBlur={() => onFocusChange?.(null)}
          style={inputStyle("fee")}
          placeholder="0.00"
          step="0.01"
          min="0"
        />
      </div>
      <div style={s.inputGroup}>
        <label style={s.label}>Description</label>
        <textarea
          value={service.description}
          onChange={(e) => onChange("description", e.target.value)}
          onFocus={() => onFocusChange?.("description")}
          onBlur={() => onFocusChange?.(null)}
          style={{ ...inputStyle("description"), ...s.textarea }}
          placeholder="Enter service description"
        />
      </div>
      <div style={s.buttonGroup}>
        <Button onClick={onCancel} variant="secondary" style={{ flex: 1 }}>
          Cancel
        </Button>
        <Button onClick={onSave} variant="primary" style={{ flex: 1 }}>
          {isEdit ? "Update Service" : "Add Service"}
        </Button>
      </div>
    </div>
  );
};

const ServicesScreen = () => {
  const { token, user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newService, setNewService] = useState({
    name: "",
    category: "",
    fee: "",
    description: "",
  });
  const [editingService, setEditingService] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(services.length / itemsPerPage);
  const currentServices = services.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const loadServices = useCallback(async () => {
    if (!token || !user?.veterinary?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchServices(token, user.veterinary.id);
      setServices(data);
    } catch (err) {
      console.error("Failed to load services:", err);
      setError("Failed to fetch services. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const getCurrencySymbol = () => {
    if (!user?.veterinary?.currency) return "";

    const currencyOption = CURRENCY_OPTIONS.find(
      (option) =>
        option.code.toUpperCase() === user.veterinary.currency.toUpperCase(),
    );

    return currencyOption ? currencyOption.symbol : user.veterinary.currency;
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await deleteService(token, serviceId);
      setServices(services.filter((service) => service.id !== serviceId));
    } catch (err) {
      console.error("Failed to delete service:", err);
      setError("Failed to delete service");
    }
  };

  const openEditModal = (service) => {
    setEditingService({ ...service });
    setIsEditModalOpen(true);
  };

  const handleInputChange = (field, value) =>
    setNewService((prev) => ({ ...prev, [field]: value }));
  const handleEditInputChange = (field, value) =>
    setEditingService((prev) => ({ ...prev, [field]: value }));

  const handleSaveService = async (serviceData, isEdit = false) => {
    try {
      if (!serviceData.name || !serviceData.category || !serviceData.fee) {
        setError("Please fill in all required fields");
        return;
      }
      const servicePayload = {
        name: serviceData.name,
        category: serviceData.category,
        description: serviceData.description,
        fee: parseFloat(serviceData.fee),
        veterinary_id: user.veterinary.id,
      };
      let result;
      if (isEdit && serviceData.id) {
        result = await updateService(token, serviceData.id, servicePayload);
        setServices(
          services.map((s) => (s.id === serviceData.id ? result : s)),
        );
        setIsEditModalOpen(false);
      } else {
        result = await createService(token, servicePayload);
        setServices([...services, result]);
        setIsAddModalOpen(false);
        setNewService({ name: "", category: "", fee: "", description: "" });
      }
      setError(null);
      return result;
    } catch (err) {
      console.error("Failed to save service:", err);
      setError(
        `Failed to ${isEdit ? "update" : "add"} service. Please try again.`,
      );
      throw err;
    }
  };

  const getCategoryIcon = (category) => {
    const categoryOption = categoryOptions.find(
      (opt) => opt.value === category,
    );
    return categoryOption ? categoryOption.icon : Syringe;
  };

  const s = styles.screen;

  if (!token)
    return (
      <div style={s.container}>
        <div style={s.errorMessage}>Please log in to view your services.</div>
      </div>
    );
  if (loading)
    return (
      <div style={s.container}>
        <div style={s.loadingMessage}>Loading services...</div>
      </div>
    );

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.headerTitle}>Services</h1>
        <div style={s.headerButtons}>
          <Button variant="ghost" icon={Bell} />
          <Button
            onClick={() => setIsAddModalOpen(true)}
            variant="primary"
            icon={Plus}
          >
            Add Service
          </Button>
        </div>
      </div>
      {error && <div style={s.error}>{error}</div>}
      <div style={s.servicesSection}>
        <h2 style={s.sectionTitle}>List of Services</h2>
        {services.length === 0 ? (
          <div style={s.emptyMessage}>
            No services found. Add your first service!
          </div>
        ) : (
          <>
            <div style={s.tableContainer}>
              <table style={s.table}>
                <thead>
                  <tr style={s.tableHeader}>
                    <th style={s.tableHeaderCell}>Service name</th>
                    <th style={s.tableHeaderCell}>Category</th>
                    <th style={s.tableHeaderCell}>Service charge</th>
                    <th style={{ ...s.tableHeaderCell, textAlign: "right" }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentServices.map((service) => {
                    const CategoryIcon = getCategoryIcon(service.category);
                    return (
                      <tr key={service.id} style={s.tableRow}>
                        <td style={s.tableCell}>
                          <div style={s.serviceCell}>
                            <div style={s.serviceIcon}>
                              <CategoryIcon style={s.serviceCellIcon} />
                            </div>
                            <div>
                              <span style={s.serviceName}>{service.name}</span>
                              {service.description && (
                                <div style={s.serviceDescription}>
                                  {service.description.length > 50
                                    ? `${service.description.substring(0, 50)}...`
                                    : service.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...s.tableCell, color: "#4b5563" }}>
                          {categoryOptions.find(
                            (cat) => cat.value === service.category,
                          )?.label || "N/A"}
                        </td>
                        <td style={{ ...s.tableCell, color: "#111827" }}>
                          {getCurrencySymbol()}
                          {typeof service.fee === "number"
                            ? service.fee.toFixed(2)
                            : service.fee}
                        </td>
                        <td style={{ ...s.tableCell, textAlign: "right" }}>
                          <Dropdown
                            trigger={
                              <Button
                                variant="ghost"
                                icon={MoreVertical}
                                size="small"
                              />
                            }
                          >
                            <DropdownItem
                              icon={Edit}
                              onClick={() => openEditModal(service)}
                            >
                              Edit
                            </DropdownItem>
                            <DropdownItem
                              icon={Trash2}
                              variant="destructive"
                              onClick={() => handleDeleteService(service.id)}
                            >
                              Delete
                            </DropdownItem>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={services.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Service"
      >
        <ServiceModal
          service={newService}
          onChange={handleInputChange}
          onSave={() => handleSaveService(newService, false)}
          onCancel={() => setIsAddModalOpen(false)}
          error={error}
          focusedInput={focusedInput}
          onFocusChange={setFocusedInput}
          getCurrencySymbol={getCurrencySymbol}
        />
      </Modal>
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingService(null);
        }}
        title="Edit Service"
      >
        {editingService && (
          <ServiceModal
            service={editingService}
            onChange={handleEditInputChange}
            onSave={() => handleSaveService(editingService, true)}
            onCancel={() => {
              setIsEditModalOpen(false);
              setEditingService(null);
            }}
            error={error}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
            isEdit
            getCurrencySymbol={getCurrencySymbol}
          />
        )}
      </Modal>
    </div>
  );
};

export default ServicesScreen;

const styles = {
  screen: {
    container: {
      minHeight: "100vh",
      backgroundColor: "#ffffff",
      padding: "24px",
      fontFamily: "system-ui, -apple-system, sans-serif",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      paddingBottom: "24px",
      borderBottom: "1px solid #e5e7eb",
    },
    headerTitle: {
      fontSize: "24px",
      fontWeight: "bold",
      color: "#111827",
    },
    headerButtons: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    error: {
      color: "#EF4444",
      fontSize: "14px",
      marginTop: "16px",
      padding: "8px",
      backgroundColor: "#FEE2E2",
      borderRadius: "4px",
      textAlign: "center",
    },
    servicesSection: {
      marginTop: "24px",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#111827",
      marginBottom: "16px",
    },
    tableContainer: {
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      overflow: "visible",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    tableHeader: {
      backgroundColor: "#ffffff",
      borderBottom: "1px solid #e5e7eb",
    },
    tableHeaderCell: {
      padding: "12px 16px",
      color: "#6b7280",
      fontWeight: "500",
      fontSize: "14px",
      textAlign: "left",
      borderBottom: "1px solid #e5e7eb",
    },
    tableRow: {
      borderBottom: "1px solid #f3f4f6",
      transition: "background-color 150ms",
    },
    tableCell: {
      padding: "16px",
      verticalAlign: "middle",
    },
    serviceCell: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    serviceIcon: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "#FFF7ED",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    serviceCellIcon: {
      width: "20px",
      height: "20px",
      color: "#F97316",
    },
    serviceName: {
      fontWeight: "500",
      color: "#111827",
    },
    serviceDescription: {
      fontSize: "14px",
      color: "#6b7280",
      marginTop: "4px",
    },
    errorMessage: {
      color: "#EF4444",
      fontSize: "14px",
      marginBottom: "16px",
      padding: "8px",
      backgroundColor: "#FEE2E2",
      borderRadius: "4px",
      textAlign: "center",
    },
    loadingMessage: {
      textAlign: "center",
      padding: "40px",
      color: "#6B7280",
      fontSize: "16px",
    },
    emptyMessage: {
      textAlign: "center",
      padding: "40px",
      color: "#6B7280",
      fontSize: "16px",
    },
  },

  button: {
    base: (size) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: size === "small" ? "6px 12px" : "10px 16px",
      borderRadius: "6px",
      fontSize: size === "small" ? "13px" : "14px",
      fontWeight: "500",
      border: "none",
      cursor: "pointer",
      transition: "all 150ms",
      fontFamily: "inherit",
    }),
    variants: {
      primary: {
        backgroundColor: "#F97316",
        color: "#ffffff",
        hover: { backgroundColor: "#EA580C" },
      },
      secondary: {
        backgroundColor: "#F3F4F6",
        color: "#374151",
        hover: { backgroundColor: "#E5E7EB" },
      },
      ghost: {
        backgroundColor: "transparent",
        color: "#6B7280",
        hover: { backgroundColor: "#F3F4F6" },
      },
    },
    icon: {
      width: "18px",
      height: "18px",
    },
  },

  modal: {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "16px",
    },
    container: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      maxWidth: "500px",
      width: "100%",
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    header: {
      padding: "20px 24px",
      borderBottom: "1px solid #E5E7EB",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    title: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#111827",
    },
    closeButton: {
      padding: "4px",
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      borderRadius: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#6B7280",
    },
    closeIcon: {
      width: "20px",
      height: "20px",
    },
    content: {
      padding: "24px",
    },
  },

  dropdown: {
    container: {
      position: "relative",
      display: "inline-block",
    },
    menu: {
      position: "absolute",
      right: 0,
      top: "calc(100% + 4px)",
      backgroundColor: "#ffffff",
      border: "1px solid #E5E7EB",
      borderRadius: "6px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
      minWidth: "160px",
      zIndex: 50,
    },
  },

  dropdownItem: {
    base: (variant, isHovered) => ({
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "14px",
      color: variant === "destructive" ? "#EF4444" : "#374151",
      backgroundColor: isHovered
        ? variant === "destructive"
          ? "#FEE2E2"
          : "#F9FAFB"
        : "transparent",
      transition: "background-color 150ms",
    }),
    icon: {
      width: "16px",
      height: "16px",
    },
  },

  pagination: {
    container: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: "20px",
      padding: "16px 0",
    },
    info: {
      fontSize: "14px",
      color: "#6B7280",
    },
    buttons: {
      display: "flex",
      gap: "8px",
    },
    button: {
      padding: "8px 12px",
      border: "1px solid #E5E7EB",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      cursor: "pointer",
      fontSize: "14px",
      color: "#374151",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      transition: "all 150ms",
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    icon: {
      width: "16px",
      height: "16px",
    },
  },

  serviceModal: {
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#374151",
    },
    required: {
      color: "#EF4444",
    },
    input: {
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
      transition: "all 150ms",
      outline: "none",
    },
    inputFocused: {
      borderColor: "#F97316",
      boxShadow: "0 0 0 3px rgba(249, 115, 22, 0.1)",
    },
    textarea: {
      minHeight: "80px",
      resize: "vertical",
      fontFamily: "inherit",
    },
    dropdownContainer: {
      position: "relative",
    },
    dropdownButton: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      transition: "all 150ms",
      outline: "none",
    },
    dropdownContent: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    dropdownIcon: {
      width: "20px",
      height: "20px",
      color: "#F97316",
    },
    dropdownText: (hasValue) => ({
      color: hasValue ? "#111827" : "#9CA3AF",
    }),
    chevron: {
      width: "16px",
      height: "16px",
      color: "#6B7280",
      transition: "transform 150ms",
    },
    chevronOpen: {
      transform: "rotate(180deg)",
    },
    dropdownMenu: {
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      right: 0,
      backgroundColor: "#fff",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      maxHeight: "240px",
      overflowY: "auto",
      zIndex: 50,
    },
    dropdownItem: {
      padding: "10px 12px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      cursor: "pointer",
      transition: "background-color 150ms",
    },
    dropdownItemHover: {
      backgroundColor: "#F9FAFB",
    },
    dropdownItemSelected: {
      backgroundColor: "#FFF7ED",
    },
    dropdownItemIcon: {
      width: "20px",
      height: "20px",
      color: "#F97316",
    },
    dropdownItemText: {
      fontSize: "14px",
      color: "#111827",
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      marginTop: "8px",
    },
  },
};
